
import { defineComponent, PropType } from 'vue';
import { PreviewOutcome, PreviewOdd } from '../../../../../types/sport';
import IllinoisCollapsible from '../Collapsible.vue';
import OddsTitle from './OddsTitle.vue';
import { GenericOption } from '../../../../../types/components';
import { i18n } from '@/plugins/vuei18n';
import OddsOutcomeColumn from './OddsOutcomeColumn.vue';
import TabBar from '../TabBar.vue';
import IllinoisCounter from '../Counter.vue';
import IllinoisIcons from '../icons.vue';
import { getCorrectData } from '@/composables/useOdds';

type BasicEventInfo = { scheduled: string; home: string; away: string };

export default defineComponent({
  name: 'OddsExpandibleCard',
  components: {
    IllinoisCollapsible,
    OddsTitle,
    OddsOutcomeColumn,
    TabBar,
    IllinoisCounter,
    IllinoisIcons
  },
  props: {
    title: {
      type: String,
      required: true
    },
    description: {
      type: String,
      required: false
    },
    odds: {
      type: Array as PropType<PreviewOdd[] | PreviewOutcome[]>,
      required: true
    },
    correctMarket: {
      type: String,
      required: false,
      default: null
    },
    correctMarket2: {
      type: String,
      required: false,
      default: null
    },
    oddWithSpecifier: {
      type: Boolean,
      required: false,
      default: false
    },
    event: {
      type: Object as PropType<BasicEventInfo>,
      required: true
    },
    spreadOptions: {
      type: Array as PropType<GenericOption[]>,
      required: false,
      default: () => []
    },
    multipleModes: {
      type: Boolean,
      required: false,
      default: false
    },
    propsColumns: {
      type: Array as PropType<string[]>,
      required: false,
      default: () => []
    },
    showTitleRow: {
      type: Boolean,
      required: false,
      default: true
    },
    isDropdownIconFixed: {
      type: Boolean,
      required: false,
      default: true
    },
    selectedSpread: {
      type: Object as PropType<GenericOption>,
      required: false,
      default: undefined
    },
    showOutcomeName: {
      type: Boolean,
      required: false,
      default: true
    },
    initialOpen: {
      type: Boolean,
      required: false,
      default: false
    },
    loading: {
      type: Boolean,
      required: false,
      default: false
    },
    isLocked: {
      type: Boolean,
      required: false,
      default: false
    },
    eventId: {
      type: String,
      required: false,
      default: undefined
    },
    sportId: {
      type: String,
      required: false,
      default: undefined
    },
    categoryId: {
      type: String,
      required: false,
      default: undefined
    },
    tournamentId: {
      type: String,
      required: false,
      default: undefined
    }
  },
  data() {
    return {
      showAll: false as boolean,
      layoutOptions: [
        {
          label: i18n.t('main') as string,
          value: 'main'
        },
        {
          label: i18n.t('all') as string,
          value: 'all'
        }
      ] as Array<{ label: string; value: string }>,
      activeTab: 'main' as string,
      result1: 0 as number,
      result2: 0 as number,
      viewportWidth: (typeof window !== 'undefined'
        ? window.innerWidth
        : 1024) as number,
      scrollLeft: 0 as number
    };
  },
  computed: {
    // Verifica se tutte le props necessarie per la navigazione sono disponibili
    canNavigateToEvent(): boolean {
      return !!(
        this.eventId &&
        this.sportId &&
        this.categoryId &&
        this.tournamentId
      );
    },
    // Genera il path per la navigazione all'evento
    eventNavigationPath(): string {
      if (!this.canNavigateToEvent) return '';
      return `/sport/${this.sportId}/category/${this.categoryId}/tournament/${this.tournamentId}/event/${this.eventId}`;
    },
    // Determina se odds è PreviewOdd[] o PreviewOutcome[]
    isPreviewOddArray(): boolean {
      if (!Array.isArray(this.odds) || this.odds.length === 0) return false;
      const firstItem = this.odds[0];
      return (
        typeof firstItem === 'object' &&
        firstItem !== null &&
        'marketName' in firstItem &&
        'outcome' in firstItem
      );
    },
    // Estrae gli outcomes da PreviewOdd[] o restituisce PreviewOutcome[]
    // Il filtraggio per spread deve essere fatto nelle pagine prima di passare i dati
    flatOutcomes(): PreviewOutcome[] {
      if (this.loading) return [];
      if (this.isPreviewOddArray) {
        const oddsArray = this.odds as PreviewOdd[];
        // Raccogli tutti gli outcome da tutti gli odds (il filtraggio è già stato fatto nelle pagine)
        const allOutcomes: PreviewOutcome[] = [];
        oddsArray.forEach(odd => {
          if (Array.isArray(odd.outcome)) {
            allOutcomes.push(
              ...odd.outcome.map(outcome => ({
                ...outcome,
                marketUniqueId: odd.unique_id
              }))
            );
          }
        });
        return allOutcomes;
      }
      return this.odds as PreviewOutcome[];
    },
    // Ottiene il primo PreviewOdd se disponibile
    firstOdd(): PreviewOdd | null {
      if (!this.isPreviewOddArray) return null;
      return (this.odds as PreviewOdd[])[0] || null;
    },
    outcomesForRender(): PreviewOutcome[][] {
      if (this.loading) {
        // 3 colonne, 1 elemento ciascuna per placeholder
        return [
          [{} as PreviewOutcome],
          [{} as PreviewOutcome],
          [{} as PreviewOutcome]
        ];
      }

      const outcomes = this.flatOutcomes;
      if (!Array.isArray(outcomes) || outcomes.length === 0) {
        return [];
      }

      const firstOdd = this.firstOdd;

      // Se è un mercato con risultato esatto (correctMarket)
      if (
        this.correctMarket &&
        firstOdd &&
        (firstOdd.id.toString() === this.correctMarket ||
          firstOdd.id.toString() === this.correctMarket2)
      ) {
        return getCorrectData(outcomes);
      }

      // Traccia tutti i marketUniqueId unici nell'ordine in cui appaiono
      const marketUniqueIds: string[] = [];
      const marketUniqueIdSet = new Set<string>();
      outcomes.forEach((outcome: PreviewOutcome) => {
        const marketId = (outcome as any).marketUniqueId || '';
        if (marketId && !marketUniqueIdSet.has(marketId)) {
          marketUniqueIds.push(marketId);
          marketUniqueIdSet.add(marketId);
        }
      });

      // Se non ci sono marketUniqueId (non è PreviewOdd[]), usa un approccio semplificato
      if (marketUniqueIds.length === 0) {
        if (
          this.oddWithSpecifier ||
          (firstOdd && firstOdd.odd_with_specifier)
        ) {
          // Raggruppa per id_outcome o outcome_name
          const groupedByOutcomeId: Record<number, PreviewOutcome[]> = {};
          const orderOfIds: number[] = [];
          outcomes.forEach((outcome: PreviewOutcome) => {
            const idOutcome = outcome.id_outcome;
            if (!groupedByOutcomeId[idOutcome]) {
              groupedByOutcomeId[idOutcome] = [];
              orderOfIds.push(idOutcome);
            }
            groupedByOutcomeId[idOutcome].push(outcome);
          });

          const allGroupsHaveOneElement = orderOfIds.every(
            id => groupedByOutcomeId[id].length === 1
          );

          if (allGroupsHaveOneElement) {
            const groupedByName: Record<string, PreviewOutcome[]> = {};
            const orderOfNames: string[] = [];
            outcomes.forEach((outcome: PreviewOutcome) => {
              const name =
                outcome.outcome_name ||
                outcome.outcome_alias ||
                outcome.name ||
                '';
              if (!groupedByName[name]) {
                groupedByName[name] = [];
                orderOfNames.push(name);
              }
              groupedByName[name].push(outcome);
            });
            return orderOfNames.map(name => groupedByName[name]);
          }
          return orderOfIds.map(id => groupedByOutcomeId[id]);
        } else {
          const total = outcomes.length;
          if (total > 4) {
            let cols = 1;
            if (total % 3 === 0) {
              cols = 3;
            } else if (total % 2 === 0) {
              cols = 2;
            } else {
              // Se non è divisibile né per 2 né per 3, usa comunque 3 colonne
              cols = 3;
            }
            const columns = this.distributeIntoColumns(outcomes, cols);
            // Normalizza le colonne aggiungendo placeholder dove necessario
            return this.normalizeColumnsWithPlaceholders(columns);
          }
          return outcomes.map(o => [o]);
        }
      }

      // Crea le colonne con placeholder dove necessario
      let groupedColumns: Record<string, Record<string, PreviewOutcome>> = {};

      if (this.oddWithSpecifier || (firstOdd && firstOdd.odd_with_specifier)) {
        // Raggruppa per id_outcome o outcome_name
        const groupedByOutcomeId: Record<
          number,
          Record<string, PreviewOutcome>
        > = {};
        const orderOfIds: number[] = [];
        outcomes.forEach((outcome: PreviewOutcome) => {
          const idOutcome = outcome.id_outcome;
          const marketId = (outcome as any).marketUniqueId || '';
          if (!groupedByOutcomeId[idOutcome]) {
            groupedByOutcomeId[idOutcome] = {};
            orderOfIds.push(idOutcome);
          }
          groupedByOutcomeId[idOutcome][marketId] = outcome;
        });

        const allGroupsHaveOneElement = orderOfIds.every(
          id => Object.keys(groupedByOutcomeId[id]).length === 1
        );

        if (allGroupsHaveOneElement) {
          // Raggruppa per outcome_name o outcome_alias
          const groupedByName: Record<
            string,
            Record<string, PreviewOutcome>
          > = {};
          const orderOfNames: string[] = [];
          outcomes.forEach((outcome: PreviewOutcome) => {
            const name =
              outcome.outcome_name ||
              outcome.outcome_alias ||
              outcome.name ||
              '';
            const marketId = (outcome as any).marketUniqueId || '';
            if (!groupedByName[name]) {
              groupedByName[name] = {};
              orderOfNames.push(name);
            }
            groupedByName[name][marketId] = outcome;
          });
          groupedColumns = groupedByName;
        } else {
          // Converti groupedByOutcomeId in formato compatibile
          orderOfIds.forEach(id => {
            groupedColumns[`id_${id}`] = groupedByOutcomeId[id];
          });
        }
      } else {
        // Caso normale: distribuisci gli outcome in colonne (logica originale)
        const total = outcomes.length;
        let columns: PreviewOutcome[][] = [];

        if (total > 4) {
          let cols = 1;
          if (total % 3 === 0) {
            cols = 3;
          } else if (total % 2 === 0) {
            cols = 2;
          } else {
            // Se non è divisibile né per 2 né per 3, usa comunque 3 colonne
            cols = 3;
          }
          columns = this.distributeIntoColumns(outcomes, cols);
          // Normalizza le colonne aggiungendo placeholder dove necessario
          columns = this.normalizeColumnsWithPlaceholders(columns);
        } else {
          // Se <= 4, crea una colonna per ogni outcome
          columns = outcomes.map(o => [o]);
        }

        // Se ci sono più odds (marketUniqueIds), normalizza le colonne aggiungendo placeholder
        if (marketUniqueIds.length > 1) {
          return this.normalizeColumnsWithMarketIds(columns, marketUniqueIds);
        }

        return columns;
      }

      // Costruisci le colonne finali con placeholder dove necessario
      const columnNames = Object.keys(groupedColumns);
      const columns: PreviewOutcome[][] = [];

      columnNames.forEach(columnName => {
        const column: PreviewOutcome[] = [];
        // Per ogni marketUniqueId nell'ordine, aggiungi l'outcome o un placeholder
        marketUniqueIds.forEach(marketId => {
          const outcome = groupedColumns[columnName][marketId];
          if (outcome) {
            column.push(outcome);
          } else {
            // Aggiungi un placeholder se manca l'outcome per questo marketId
            column.push(this.createFillOutcome());
          }
        });
        columns.push(column);
      });

      return columns;
    },
    columns(): string[] {
      if (this.loading) {
        return this.propsColumns.length > 0 ? this.propsColumns : [];
      }

      const columns: string[] = [];

      // Helper per aggiungere una colonna solo se non è già presente
      const addColumnIfNotExists = (name: string) => {
        if (name && columns.indexOf(name) === -1) {
          columns.push(name);
        }
      };

      // Se odds è PreviewOdd[], verifica tutti gli outcome di tutte le odds
      if (this.isPreviewOddArray) {
        const oddsArray = this.odds as PreviewOdd[];
        if (oddsArray.length > 0 && oddsArray[0].outcome) {
          const firstOdd = oddsArray[0];
          // Se è correctMarket, usa i nomi speciali
          if (
            this.correctMarket &&
            (firstOdd.id.toString() === this.correctMarket ||
              firstOdd.id.toString() === this.correctMarket2)
          ) {
            addColumnIfNotExists(this.event.home);
            addColumnIfNotExists(this.$t('tie'));
            addColumnIfNotExists(this.event.away);
          } else {
            // Itera su tutte le odds e tutti i loro outcome per raccogliere tutti i nomi unici
            oddsArray.forEach(odd => {
              if (odd.outcome && Array.isArray(odd.outcome)) {
                odd.outcome.forEach(outcome => {
                  const name =
                    outcome.outcome_name || outcome.outcome_alias || '';
                  addColumnIfNotExists(name);
                });
              }
            });
          }
        }
      } else {
        // Se odds è PreviewOutcome[], usa i nomi degli outcomes mantenendo l'ordine originale
        const outcomes = this.flatOutcomes;
        if (outcomes.length > 0) {
          // Se è correctMarket, usa i nomi speciali
          const firstOdd = this.firstOdd;
          if (
            firstOdd &&
            this.correctMarket &&
            (firstOdd.id.toString() === this.correctMarket ||
              firstOdd.id.toString() === this.correctMarket2)
          ) {
            addColumnIfNotExists(this.event.home);
            addColumnIfNotExists(this.$t('tie'));
            addColumnIfNotExists(this.event.away);
          } else {
            // Estrai i nomi direttamente dagli outcomes per mantenere l'ordine originale
            outcomes.forEach(outcome => {
              const name = outcome.outcome_name || outcome.outcome_alias || '';
              addColumnIfNotExists(name);
            });
          }
        }
      }

      // Se abbiamo colonne calcolate, restituiscile
      if (columns.length > 0) {
        return columns;
      }

      // Fallback a propsColumns se disponibile
      return this.propsColumns.length > 0 ? this.propsColumns : [];
    },
    outcomes(): PreviewOutcome[] {
      return this.flatOutcomes;
    },
    selectedOutcome(): PreviewOutcome | undefined {
      const outcomeAlias = this.result1 + '-' + this.result2;
      return this.outcomes.find(
        outcome =>
          outcome.alias === outcomeAlias ||
          outcome.outcome_name === outcomeAlias
      );
    },
    oddValue(): string | undefined {
      return this.selectedOutcome?.outcome_odd_value as unknown as
        | string
        | undefined;
    },
    oddsSources() {
      return this.$store.getters['cart/getOddsSources'];
    },
    parsedOutcomePairs(): Array<{ home: number; away: number }> {
      const pairs: Array<{ home: number; away: number }> = [];
      for (const outcome of this.outcomes) {
        const parsed = this.parseOutcomeScores(outcome);
        if (parsed) pairs.push(parsed);
      }
      return pairs;
    },
    canIncrementHome(): boolean {
      // Permetti incremento se esiste un outcome con stesso away e home maggiore
      const targetAway = this.result2;
      return this.parsedOutcomePairs.some(
        pair => pair.away === targetAway && pair.home > this.result1
      );
    },
    canIncrementAway(): boolean {
      // Permetti incremento se esiste un outcome con stesso home e away maggiore
      const targetHome = this.result1;
      return this.parsedOutcomePairs.some(
        pair => pair.home === targetHome && pair.away > this.result2
      );
    }
  },
  methods: {
    $t(key: string, values?: Record<string, unknown> | any[]): string {
      return i18n.t(key, values) as string;
    },
    handleSpreadChange(item: GenericOption) {
      this.$emit('spread-change', item);
    },
    handleTabSelected(tab: string) {
      this.activeTab = tab;
    },
    findAndAddOddToCart(outcomes: PreviewOutcome[]) {
      const outcome = outcomes.find(
        outcome =>
          outcome.alias ||
          outcome.outcome_name === this.result1 + '-' + this.result2
      );
      this.$emit('add-odd-to-cart', {
        outcomeUniqueId: outcome?.unique_id,
        outcomeId: outcome?.outcome_id
      });
    },
    emit(event: string, ...args: any[]) {
      this.$emit(event, ...args);
    },
    initializeResultsFromOutcomes() {
      if (this.loading) return;
      const hasCurrent = this.parsedOutcomePairs.some(
        pair => pair.home === this.result1 && pair.away === this.result2
      );
      if (hasCurrent) return;
      const firstPair = this.parsedOutcomePairs[0];
      if (firstPair) {
        this.result1 = firstPair.home;
        this.result2 = firstPair.away;
      }
    },
    parseOutcomeScores(
      outcome: PreviewOutcome
    ): { home: number; away: number } | null {
      const label = (outcome.alias || outcome.outcome_name) as unknown as
        | string
        | undefined;
      if (!label) return null;
      const match = label.match(/^(\d+)\s*-\s*(\d+)$/);
      if (!match) return null;
      const home = Number(match[1]);
      const away = Number(match[2]);
      if (Number.isNaN(home) || Number.isNaN(away)) return null;
      return { home, away };
    },
    onResize() {
      if (typeof window === 'undefined') return;
      this.viewportWidth = window.innerWidth;
      if (this.multipleModes && this.viewportWidth < 768) {
        this.activeTab = 'all';
      }
    },
    isOutcomeOpen(outcome: PreviewOutcome): boolean {
      return outcome.status === 1;
    },
    distributeIntoColumns(
      items: PreviewOutcome[],
      numCols: number
    ): PreviewOutcome[][] {
      const columns: PreviewOutcome[][] = [];
      if (!items || items.length === 0 || numCols <= 0) return columns;
      const total = items.length;
      const base = Math.floor(total / numCols);
      const remainder = total % numCols;
      let start = 0;
      for (let i = 0; i < numCols; i++) {
        const size = base + (i < remainder ? 1 : 0);
        const end = start + size;
        if (size > 0) {
          columns.push(items.slice(start, end));
        }
        start = end;
      }
      return columns;
    },
    createFillOutcome(): PreviewOutcome {
      // Crea un elemento fill che verrà visualizzato con il lock
      return {
        unique_id: '',
        outcome_id: 0,
        outcome_odd_value: '',
        status: 0, // Status diverso da 1 per mostrare il lock
        name: '',
        outcome_alias: '',
        outcome_name: '',
        id_outcome: 0,
        ext_id: '',
        lastUpdate: '',
        sort: 0,
        alias: '',
        odd_percent: '',
        outcome_odd_value_for_order: '',
        active: false
      } as PreviewOutcome;
    },
    normalizeColumnsWithMarketIds(
      columns: PreviewOutcome[][],
      marketUniqueIds: string[]
    ): PreviewOutcome[][] {
      if (!columns || columns.length === 0 || marketUniqueIds.length === 0) {
        return columns;
      }

      // Raggruppa gli outcome per colonna e marketUniqueId
      const normalizedColumns: PreviewOutcome[][] = [];

      columns.forEach(column => {
        // Crea una mappa per tracciare quale outcome appartiene a quale marketUniqueId
        const outcomeByMarketId: Record<string, PreviewOutcome> = {};
        column.forEach(outcome => {
          const marketId = (outcome as any).marketUniqueId || '';
          if (marketId) {
            outcomeByMarketId[marketId] = outcome;
          }
        });

        // Costruisci la colonna normalizzata con un elemento per ogni marketUniqueId
        const normalizedColumn: PreviewOutcome[] = [];
        marketUniqueIds.forEach(marketId => {
          const outcome = outcomeByMarketId[marketId];
          if (outcome) {
            normalizedColumn.push(outcome);
          } else {
            // Aggiungi un placeholder se manca l'outcome per questo marketId
            normalizedColumn.push(this.createFillOutcome());
          }
        });

        normalizedColumns.push(normalizedColumn);
      });

      return normalizedColumns;
    },
    normalizeColumns(columns: PreviewOutcome[][]): PreviewOutcome[][] {
      if (!columns || columns.length === 0) return columns;

      // Trova il numero massimo di righe tra tutte le colonne
      const maxRows = Math.max(...columns.map(col => col.length));

      // Se tutte le colonne hanno già la stessa lunghezza, non serve normalizzare
      if (columns.every(col => col.length === maxRows)) {
        return columns;
      }

      // Trasforma le colonne in righe per normalizzare riga per riga
      const rows: (PreviewOutcome | null)[][] = [];
      for (let rowIndex = 0; rowIndex < maxRows; rowIndex++) {
        const row: (PreviewOutcome | null)[] = [];
        for (let colIndex = 0; colIndex < columns.length; colIndex++) {
          const column = columns[colIndex];
          // Se la colonna ha un elemento a questa riga, usalo, altrimenti null
          row.push(rowIndex < column.length ? column[rowIndex] : null);
        }
        rows.push(row);
      }

      // Trova il numero massimo di colonne per riga (dovrebbe essere columns.length)
      const maxCols = columns.length;

      // Normalizza ogni riga aggiungendo elementi fill dove necessario
      const normalizedRows = rows.map(row => {
        const normalizedRow: PreviewOutcome[] = [];
        for (let i = 0; i < maxCols; i++) {
          // Se c'è un elemento nella posizione, usalo, altrimenti aggiungi fill
          normalizedRow.push(row[i] || this.createFillOutcome());
        }
        return normalizedRow;
      });

      // Trasforma di nuovo le righe in colonne
      const normalizedColumns: PreviewOutcome[][] = [];
      for (let colIndex = 0; colIndex < maxCols; colIndex++) {
        const column: PreviewOutcome[] = [];
        for (let rowIndex = 0; rowIndex < normalizedRows.length; rowIndex++) {
          column.push(normalizedRows[rowIndex][colIndex]);
        }
        normalizedColumns.push(column);
      }

      return normalizedColumns;
    },
    normalizeColumnsWithPlaceholders(
      columns: PreviewOutcome[][]
    ): PreviewOutcome[][] {
      if (!columns || columns.length === 0) return columns;

      // Trova il numero massimo di righe tra tutte le colonne
      const maxRows = Math.max(...columns.map(col => col.length));

      // Se tutte le colonne hanno già la stessa lunghezza, non serve normalizzare
      if (columns.every(col => col.length === maxRows)) {
        return columns;
      }

      // Normalizza ogni colonna aggiungendo placeholder dove necessario
      return columns.map(column => {
        const normalizedColumn = [...column];
        // Aggiungi placeholder fino a raggiungere la lunghezza massima
        while (normalizedColumn.length < maxRows) {
          normalizedColumn.push(this.createFillOutcome());
        }
        return normalizedColumn;
      });
    },
    getSpreadFromUnique(uniqueId: string): string | null {
      const s = String(uniqueId || '');
      const idx = s.indexOf('#');
      return idx >= 0 ? s.substring(idx + 1) : null;
    },
    addOddToCart(e: any) {
      console.log('addOddToCart', e);
      let marketId = e.marketId;

      // Se marketId non è presente, cerca nelle colonne di outcomes
      if (!marketId && e.outcomeUniqueId) {
        const columns = this.outcomesForRender;
        for (const column of columns) {
          const outcome = column.find(o => o.unique_id === e.outcomeUniqueId);
          if (outcome?.marketUniqueId) {
            marketId = outcome.marketUniqueId;
            break;
          }
        }
      }

      this.$emit('add-odd-to-cart', {
        ...e,
        marketId
      });
    },
    handleScrollChange(scrollLeft: number) {
      this.scrollLeft = scrollLeft;
    }
  },
  mounted() {
    if (typeof window !== 'undefined') {
      window.addEventListener(
        'resize',
        this.onResize as any,
        { passive: true } as any
      );
      if (this.multipleModes && this.viewportWidth < 768) {
        this.activeTab = 'all';
      }
      this.$nextTick(() => this.initializeResultsFromOutcomes());
    }
  },
  watch: {
    loading(newVal: boolean) {
      if (!newVal) this.$nextTick(() => this.initializeResultsFromOutcomes());
    },
    odds: {
      handler() {
        this.$nextTick(() => this.initializeResultsFromOutcomes());
      },
      deep: true
    }
  },
  beforeDestroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.onResize as any);
    }
  }
});
